import axios from 'axios';
import { getApiUrl } from '../../config/api';

// Configurar Axios para manejar CORS
axios.defaults.withCredentials = true;

// Interceptor para manejar errores
axios.interceptors.response.use(
  response => response,
  error => {
    console.error('Error en la solicitud Axios:', error);
    if (error.message && error.message.includes('Network Error')) {
      console.warn('Posible error de CORS. Verifica la configuración del servidor.');
    }
    return Promise.reject(error);
  }
);

// Claves para almacenar información de sesión en localStorage
const TOKEN_KEY = 'user_session';
const USER_TOKEN = 'user_token';
const USER_MENU = 'user_menu';

/**
 * Servicio de autenticación para manejar login, logout y estado de sesión
 */
export default {
  /**
   * Iniciar sesión con credenciales de usuario
   * @param {string} usuarioId - ID del usuario
   * @param {string} password - Contraseña del usuario
   * @returns {Promise} - Promesa con el resultado del login
   */
  async login(usuarioId, password) {
    try {
      // Construir la URL completa para debugging
      const loginUrl = getApiUrl('/api/AAcceso/Login');
      console.log('URL de login:', loginUrl);
      
      // Usar el endpoint de CrudApi para el login
      const response = await axios.post(loginUrl, {
        UsuarioId: usuarioId,
        Password: password
      }, {
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        timeout: 10000 // 10 segundos de timeout
      });
      
      console.log('Respuesta completa del login:', response.data);
      
      // Adaptar la respuesta al formato esperado por la aplicación
      // El controlador AAccesoController devuelve un MgkMessage
      const loginResult = {
        Success: response.data.Number > 0,
        Message: response.data.Message,
        Usuario: response.data.OData,
        Token: response.data.Code || ''
      };
      
      if (loginResult.Success) {
        console.log("Login exitoso");
        console.log('Estructura del objeto Usuario:', loginResult.Usuario);
        
        // Guardar información de sesión en localStorage
        localStorage.setItem(TOKEN_KEY, JSON.stringify(loginResult));
        console.log('Guardado en localStorage:', JSON.parse(localStorage.getItem(TOKEN_KEY)));
        
        // Guardar el token por separado para fácil acceso
        localStorage.setItem(USER_TOKEN, loginResult.Token);
        
        // Guardar el menú del usuario si existe
        console.log('Estructura completa del usuario:', loginResult.Usuario);
        
        // Basado en la estructura exacta de la respuesta que vimos
        console.log('Intentando encontrar el menú en la estructura de la respuesta...');
        
        // La estructura correcta según la respuesta que vimos
        // response.data.OData._UsuarioMo._MenuList
        if (response.data && 
            response.data.OData && 
            response.data.OData._UsuarioMo && 
            Array.isArray(response.data.OData._UsuarioMo._MenuList)) {
          
          const menuList = response.data.OData._UsuarioMo._MenuList;
          console.log('Menú del usuario encontrado en la respuesta:', menuList);
          localStorage.setItem(USER_MENU, JSON.stringify(menuList));
        } else {
          console.error('No se encontró la estructura de menú esperada');
          console.log('Estructura de la respuesta:', response.data);
        }
      }
      
      return loginResult;
    } catch (error) {
      console.error('Error de login:', error);
      
      if (error.response) {
        // El servidor respondió con un código de estado fuera del rango 2xx
        return error.response.data;
      } else {
        // Error de red o error al configurar la solicitud
        return {
          Success: false,
          Message: 'Error de conexión. Por favor, intente nuevamente.'
        };
      }
    }
  },
  
  /**
   * Cerrar sesión del usuario actual
   * @returns {Promise} - Promesa que se resuelve cuando se completa el logout
   */
  async logout() {
    try {
      const token = localStorage.getItem(USER_TOKEN);
      
      if (token) {
        // Notificar al backend sobre el logout para registrar la desconexión
        await axios.post(getApiUrl('/api/AAcceso/Borrar/'), { Token: token }, {
          headers: {
            'Content-Type': 'application/json'
          }
        });
      }
    } catch (error) {
      console.error('Error al registrar logout en el servidor:', error);
    } finally {
      // Eliminar información de sesión
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(USER_TOKEN);
      localStorage.removeItem(USER_MENU);
      localStorage.removeItem('empresa_seleccionada'); // Limpiar empresa seleccionada
    }
  },
  
  /**
   * Verificar si hay un usuario autenticado
   * @returns {boolean} - true si hay un usuario autenticado, false en caso contrario
   */
  isAuthenticated() {
    const userSession = localStorage.getItem(TOKEN_KEY);
    return !!userSession; // Convertir a booleano
  },
  
  /**
   * Obtener información del usuario autenticado
   * @returns {Object|null} - Información del usuario o null si no hay sesión
   */
  getUser() {
    const userSession = localStorage.getItem(TOKEN_KEY);
    if (userSession) {
      return JSON.parse(userSession);
    }
    return null;
  },
  
  /**
   * Obtener el token de sesión actual
   * @returns {string|null} - Token de sesión o null si no hay sesión
   */
  getToken() {
    return localStorage.getItem(USER_TOKEN);
  },

  /**
   * Obtener el menú del usuario autenticado
   * @returns {Array|null} - Array con los elementos del menú o null si no hay menú
   */
  getUserMenu() {
    const menuData = localStorage.getItem(USER_MENU);
    return menuData ? JSON.parse(menuData) : null;
  },

  /**
   * Obtener los datos completos del usuario autenticado, incluyendo menús y módulos
   * @returns {Object|null} - Datos completos del usuario o null si no hay sesión
   */
  getUserData() {
    const userSession = localStorage.getItem(TOKEN_KEY);
    if (!userSession) {
      return null;
    }
    
    try {
      const userData = JSON.parse(userSession);
      return userData.Usuario; // Contiene _UsuarioMo con _MenuList y _ModuloList
    } catch (error) {
      console.error('Error al parsear los datos del usuario:', error);
      return null;
    }
  },

  /**
   * Obtener la empresa seleccionada
   * @returns {Object|null} - Empresa seleccionada o null si no hay ninguna
   */
  getSelectedEmpresa() {
    const empresaData = localStorage.getItem('empresa_seleccionada');
    return empresaData ? JSON.parse(empresaData) : null;
  },

  /**
   * Establecer la empresa seleccionada
   * @param {Object} empresa - Objeto empresa a guardar
   * @returns {Promise} - Promesa con el resultado de la operación
   */
  async setSelectedEmpresa(empresa) {
    try {
      const token = this.getToken();
      
      if (!token) {
        throw new Error('No hay token de autenticación disponible');
      }

      // Notificar al backend sobre la selección de empresa
      const response = await axios.post(getApiUrl('/api/AAcceso/ActualizarSessionEmpresa'), {
        EmpresaId: empresa.EmpresaId
      }, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      console.log('Empresa actualizada en el backend:', response.data);
      
      // Guardar en localStorage solo si la petición al backend fue exitosa
      localStorage.setItem('empresa_seleccionada', JSON.stringify(empresa));
      
      return {
        success: true,
        message: 'Empresa seleccionada correctamente'
      };
      
    } catch (error) {
      console.error('Error al actualizar empresa en el backend:', error);
      
      // Aún así guardar en localStorage para que la aplicación funcione
      localStorage.setItem('empresa_seleccionada', JSON.stringify(empresa));
      
      return {
        success: false,
        message: 'Error al notificar al backend, pero empresa guardada localmente',
        error: error.message
      };
    }
  },

  /**
   * Limpiar la empresa seleccionada
   */
  clearSelectedEmpresa() {
    localStorage.removeItem('empresa_seleccionada');
  },

  /**
   * Verificar si el usuario tiene empresas activas
   * @returns {boolean} - true si tiene empresas activas, false en caso contrario
   */
  hasActiveEmpresas() {
    const userData = this.getUserData();
    if (userData && userData._UsuarioMo && userData._UsuarioMo._EmpresaList) {
      const empresasActivas = userData._UsuarioMo._EmpresaList.filter(empresa => empresa.EsActivo === true);
      return empresasActivas.length > 0;
    }
    return false;
  }
};
